/* eslint-disable react-hooks/exhaustive-deps */
import {Checkbox, Col, Radio, Row} from "antd"
import cx from "classnames"
import {BaseInput} from "components"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import React, {useMemo} from "react"
import {useTranslation} from "react-i18next"
import {ContactRole, UserAutocomplete} from "types/user"
import {settingEmailTemplatesService} from "services"
import {getFullName} from "helpers"
import styles from "../Workflow.module.css"
import RichTextEditor from "components/RichTextEditor/RichTextEditor"
import {useModel} from "hooks"
import {Descendant} from "slate"
import {INITIAL_RTE_CONTENT} from "components/RichTextEditor/slate-custom-types"
import {EmailTemplate} from "types/settings/email-template"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import UserSelect from "components/UserSelect"
import {omit, pick} from "lodash"
import {DepartmentSubunit} from "types/departments"
import {Auth} from "types/auth"
import {useCanSendSms} from "hooks/useSmsSenders"

export enum ActionMessageRecipientType {
    Student = "student",
    Staff = "staff"
}

export enum ActionMessageSenderType {
    Profile = "profile",
    Role = "role",
    Department = "department"
}

export enum ActionMessageStaffType {
    Profile = "profile",
    Role = "role"
}

type MessageFormProps = {
    initialValue: string
    onChange: (value: string) => void
    staffRoles: ContactRole[]
    subunits: DepartmentSubunit[]
    isNotifOnly?: boolean
}

type ActionMessageData = {
    recipientTypes: ActionMessageRecipientType[]
    studentRecipient?: {
        senderType: ActionMessageSenderType
        senderProfile?: UserAutocomplete
        senderRoleId?: number
    }
    staffRecipient?: {
        staffType: ActionMessageStaffType
        profiles?: UserAutocomplete[]
        roleIds?: number[]
    }
    sendAsSms: boolean
    sendAsEmail: boolean
    saveToActivity: boolean
    template?: Partial<EmailTemplate>
    subject?: string
    htmlContent: string
    autoSyncTemplate: boolean

    // DEPRECATED
    senderType?: ActionMessageSenderType
    senderProfile?: UserAutocomplete
    senderRoleId?: number
}

export const MessageForm: React.FC<MessageFormProps> = ({
    initialValue,
    onChange,
    staffRoles,
    subunits,
    isNotifOnly = false
}) => {
    const {t} = useTranslation(["workflow", "common"])
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const userTimeFormat = model.getUserTimeFormat()
    const initialData = React.useMemo(() => JSON.parse(initialValue || "{}"), [])
    const [data, setData] = React.useState<ActionMessageData>({
        recipientTypes: [ActionMessageRecipientType.Student],
        studentRecipient: {
            senderType: initialData.senderType ?? ActionMessageSenderType.Profile,
            senderRoleId: initialData.senderRoleId,
            senderProfile: initialData.senderProfile
        },
        staffRecipient: {
            staffType: ActionMessageStaffType.Profile,
            profiles: [],
            roleIds: []
        },
        sendAsSms: false,
        sendAsEmail: false,
        saveToActivity: false,
        template: undefined,
        subject: "",
        htmlContent: "",
        autoSyncTemplate: false,
        ...omit(initialData, ["senderType", "senderRoleId", "senderProfile"])
    })
    const [templates, setTemplates] = React.useState<EmailTemplate[]>([])
    const [descendants, setDescendants] = React.useState<Descendant[]>(
        initialData.htmlContent ? JSON.parse(initialData.htmlContent) : INITIAL_RTE_CONTENT
    )

    const canSendSms = useCanSendSms({
        userId:
            data.studentRecipient.senderType === ActionMessageSenderType.Profile
                ? data.studentRecipient.senderProfile?.userId
                : undefined,
        staffRoleId: [ActionMessageSenderType.Role, ActionMessageSenderType.Department].includes(
            data.studentRecipient.senderType
        )
            ? data.studentRecipient.senderRoleId
            : undefined,
        departmentIds: subunits.map((subunit) => subunit.departmentId)
    })

    React.useEffect(() => {
        if (data.sendAsSms && !canSendSms) {
            setData((prev) => ({...prev, sendAsSms: false}))
        }
    }, [canSendSms])

    const canUseDepartmentChat = subunits.every((subunit) =>
        model.departmentsChats.find((dept) => dept.id === subunit.departmentId && dept.isDepartmentalChatEnabled)
    )

    React.useEffect(() => {
        onChange(
            JSON.stringify({
                ...data,
                htmlContent: JSON.stringify(descendants)
            })
        )
    }, [onChange, descendants, data])

    React.useEffect(() => {
        ;(async function loadTemplates() {
            const {data} = await settingEmailTemplatesService.list({
                range: {page: 1, pageSize: UNLIMITED_PAGE_SIZE},
                filter: {},
                linkedObjects: false
            })
            const dataFormated = data.map(({title, ...rest}) => ({...rest, title: `${title} (${rest.code})`}))
            setTemplates(dataFormated)
        })()
    }, [])

    const changeData = React.useCallback((values: Partial<ActionMessageData>) => {
        setData((prev) => ({...prev, ...values}))
    }, [])

    const changeStudentRecipient = React.useCallback((values: Partial<ActionMessageData["studentRecipient"]>) => {
        setData((prev) => ({
            ...prev,
            studentRecipient: {...prev.studentRecipient, ...values}
        }))
    }, [])

    const changeStaffRecipient = React.useCallback((values: Partial<ActionMessageData["staffRecipient"]>) => {
        setData((prev) => ({
            ...prev,
            staffRecipient: {...prev.staffRecipient, ...values}
        }))
    }, [])

    const studentSenderView = (
        <Col span={12}>
            <h3 className={styles.autoWorkflow__section__body__title}>{t(`workflow.userProfileType.student`)}</h3>
            <FormLabel label={t("workflow.label.message.sender")} isRequired />
            <Radio.Group
                value={data.studentRecipient?.senderType}
                onChange={(event) => changeStudentRecipient({senderType: event.target.value})}
                className="mb-8">
                <Radio value={ActionMessageSenderType.Profile}>{t("workflow.label.specificStaff")}</Radio>
                <Radio value={ActionMessageSenderType.Role}>{t("workflow.label.studentRelationship")}</Radio>
                {canUseDepartmentChat && (
                    <Radio value={ActionMessageSenderType.Department}>{t("workflow.label.department")}</Radio>
                )}
            </Radio.Group>

            {data.studentRecipient?.senderType === ActionMessageSenderType.Profile && (
                <UserSelect
                    placeholder={t("workflow.label.selectStaff")}
                    type={Auth.UserProfileType.Staff}
                    value={data.studentRecipient?.senderProfile}
                    onChange={(senderProfile) => changeStudentRecipient({senderProfile})}
                />
            )}

            {data.studentRecipient?.senderType !== ActionMessageSenderType.Profile && (
                <KlassDropdown
                    placeholder={t("workflow.label.studentRelationship")}
                    valueKey="id"
                    labelKey="name"
                    options={staffRoles}
                    value={staffRoles.find((role) => role.id === data.studentRecipient?.senderRoleId)}
                    onChange={(option?: ContactRole) => changeStudentRecipient({senderRoleId: option?.id ?? null})}
                />
            )}
        </Col>
    )

    const staffTypeView = (
        <Col span={12}>
            <h3 className={styles.autoWorkflow__section__body__title}>{t(`workflow.userProfileType.staff`)}</h3>
            <FormLabel label={t("workflow.label.message.staff")} isRequired />
            <Radio.Group
                value={data.staffRecipient?.staffType}
                onChange={(event) => changeStaffRecipient({staffType: event.target.value})}
                className="mb-8">
                <Radio value={ActionMessageStaffType.Profile}>{t("workflow.label.specificStaff")}</Radio>
                <Radio value={ActionMessageStaffType.Role}>{t("workflow.label.studentRelationship")}</Radio>
            </Radio.Group>

            {data.staffRecipient?.staffType === ActionMessageStaffType.Profile && (
                <UserSelect
                    isMulti
                    placeholder={t("workflow.label.selectStaff")}
                    type={Auth.UserProfileType.Staff}
                    value={data.staffRecipient?.profiles}
                    onChange={(options) => changeStaffRecipient({profiles: options ?? []})}
                />
            )}

            {data.staffRecipient?.staffType === ActionMessageStaffType.Role && (
                <KlassDropdown
                    isMulti
                    placeholder={t("workflow.label.studentRelationship")}
                    valueKey="id"
                    labelKey="name"
                    options={staffRoles}
                    value={staffRoles.filter((role) => data.staffRecipient?.roleIds?.includes(role.id))}
                    onChange={(options?: ContactRole[]) =>
                        changeStaffRecipient({roleIds: (options ?? []).map((option) => option.id)})
                    }
                />
            )}
        </Col>
    )

    return (
        <Row gutter={[40, 16]}>
            <Col span={24}>
                <FormLabel label={t("workflow.label.message.recipients")} />
                <Checkbox.Group
                    options={[
                        {
                            value: ActionMessageRecipientType.Student,
                            label: t(`workflow.userProfileType.${Auth.UserProfileType.Student}`)
                        },
                        {
                            value: ActionMessageRecipientType.Staff,
                            label: t(`workflow.userProfileType.${Auth.UserProfileType.Staff}`)
                        }
                    ]}
                    value={data.recipientTypes}
                    onChange={(values) => changeData({recipientTypes: values as ActionMessageRecipientType[]})}
                    className={styles.checkboxGroup}
                />
            </Col>

            {data.recipientTypes?.includes(ActionMessageRecipientType.Student) && studentSenderView}
            {data.recipientTypes?.includes(ActionMessageRecipientType.Staff) && staffTypeView}

            {!isNotifOnly && (
                <Col span={24}>
                    <FormLabel label={t("workflow.label.message.template")} />
                    <KlassDropdown
                        placeholder={t("workflow.hint.select")}
                        options={templates}
                        labelKey="title"
                        valueKey="emailTemplateId"
                        isClearable
                        value={data.template}
                        onChange={(option?: EmailTemplate) => {
                            changeData({
                                template: option ? pick(option, ["emailTemplateId", "title", "editorType"]) : null,
                                subject: option?.subject ? option.subject : data.subject,
                                autoSyncTemplate: false
                            })
                            setDescendants((prev) => (option?.template ? JSON.parse(option.template) : prev))
                        }}
                    />
                </Col>
            )}

            {!isNotifOnly && data.sendAsEmail && (
                <Col span={24}>
                    <FormLabel label={t("workflow.label.message.subject")} />
                    <BaseInput value={data.subject} onChange={(subject) => changeData({subject})} />
                </Col>
            )}

            <Col span={24}>
                <FormLabel label={t("workflow.label.message.message")} />
                <RichTextEditor
                    className={styles.rteWrappter}
                    placeholder={t("workflow.hint.notes")}
                    value={descendants}
                    onChange={setDescendants}
                    userDateFormat={userDateFormat}
                    userTimeFormat={userTimeFormat}
                    dataFieldInsertable
                    fieldReadOnly
                    editorType={data.template?.editorType}
                />
            </Col>

            {!isNotifOnly && (
                <Col span={24} className="flex flex-row space-x-16">
                    <div
                        className={cx(styles.checkbox, "py-4 px-6 text-16 leading-normal", {
                            [styles.active]: data.sendAsSms
                        })}>
                        <Checkbox
                            disabled={!canSendSms}
                            checked={data.sendAsSms}
                            onChange={(event) => changeData({sendAsSms: event.target.checked})}
                        />
                        <p>{t("workflow.label.message.alsoSendAsSms")}</p>
                    </div>

                    <div
                        className={cx(styles.checkbox, "py-4 pl-6 pr-12 text-16 leading-normal", {
                            [styles.active]: data.sendAsEmail
                        })}>
                        <Checkbox
                            checked={data.sendAsEmail}
                            onChange={(event) => changeData({sendAsEmail: event.target.checked})}
                        />
                        <p>{t("workflow.label.message.alsoSendAsEmail")}</p>
                    </div>

                    {data.studentRecipient?.senderType === ActionMessageSenderType.Department && (
                        <div
                            className={cx(styles.checkbox, "py-4 pl-6 pr-12 text-16 leading-normal", {
                                [styles.active]: data.saveToActivity
                            })}>
                            <Checkbox
                                checked={data.saveToActivity}
                                onChange={(event) => changeData({saveToActivity: event.target.checked})}
                            />
                            <p>{t("workflow.label.message.saveToActivity")}</p>
                        </div>
                    )}
                </Col>
            )}

            {!isNotifOnly && !!data.template && (
                <Col span={24}>
                    <div
                        className={cx(styles.checkbox, "py-4 pl-6 pr-12 text-16 leading-normal", {
                            [styles.active]: data.autoSyncTemplate
                        })}>
                        <Checkbox
                            checked={data.autoSyncTemplate}
                            onChange={(event) => changeData({autoSyncTemplate: event.target.checked})}
                        />
                        <p>{t("workflow.label.message.autoSyncTemplate")}</p>
                    </div>
                </Col>
            )}
        </Row>
    )
}

interface MessageViewProps {
    value: string
    staffRoles: ContactRole[]
}

export const MessageView: React.FC<MessageViewProps> = ({value, staffRoles}) => {
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const userTimeFormat = model.getUserTimeFormat()

    const data = React.useMemo(() => JSON.parse(value || "{}") as ActionMessageData, [value])
    const {
        htmlContent,
        recipientTypes = [ActionMessageRecipientType.Student],
        studentRecipient = {
            senderType: data.senderType,
            senderProfile: data.senderProfile,
            senderRoleId: data.senderRoleId
        },
        staffRecipient
    } = data
    const descendants = React.useMemo(
        () => (htmlContent ? JSON.parse(htmlContent) : INITIAL_RTE_CONTENT),
        [htmlContent]
    )

    return (
        <div className="space-y-8">
            <h5 className={styles.autoWorkflow__section__actionView__content__heading}>
                {recipientTypes?.includes(ActionMessageRecipientType.Student) && (
                    <div>
                        To student, from:{" "}
                        {studentRecipient?.senderType === ActionMessageSenderType.Profile
                            ? getFullName(studentRecipient?.senderProfile)
                            : studentRecipient?.senderType === ActionMessageSenderType.Role
                            ? staffRoles.find((role) => role.id === studentRecipient?.senderRoleId)?.name
                            : "Department"}
                    </div>
                )}
                {recipientTypes?.includes(ActionMessageRecipientType.Staff) && (
                    <div>
                        To staff:{" "}
                        {staffRecipient?.staffType === ActionMessageStaffType.Profile
                            ? staffRecipient?.profiles?.map(getFullName)?.join(", ")
                            : staffRoles
                                  .filter((role) => staffRecipient?.roleIds?.includes(role.id))
                                  .map((role) => role.name)
                                  .join(", ")}
                    </div>
                )}
            </h5>

            <RichTextEditor
                value={descendants}
                userDateFormat={userDateFormat}
                userTimeFormat={userTimeFormat}
                readOnly
                fieldReadOnly
            />
        </div>
    )
}
